import { useMemo, useState } from 'react'
import { Link, useLoaderData, useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import {
  Button, Chip, Typography, Card, CardBody, Input, Select, Option, Textarea
} from '@material-tailwind/react'
import { EmployeeSelector } from '@/shared/selectors/employee-selector'
import { CompanySelector } from '@/shared/selectors/company-selector'
import { ContractSelector } from '@/shared/selectors/contract-selector'
import { OfficeSelector } from '@/shared/selectors/office-selector'
import { DateRangeCell } from '@/shared/components/date-range-cell'
import { formatDateTimeWithSeconds } from '@/shared/components/datetime'


const normalize = (r) => ({
  id: r.accountScopedId,
  subject_type: r.subject_type ?? r.subjectType,
  subject_id: r.subject_id ?? r.subjectId,
  subject_name: r.subject_name ?? r.subjectName,
  subject_subtitle: r.subject_subtitle ?? r.subjectSubtitle,
  office_id: r.office_id ?? r.officeId,
  office_name: r.office_name ?? r.officeName,
  kind: r.kind,
  justification: r.justification,
  cancel_reason: r.cancel_reason ?? r.cancelReason,
  granted_by_name: r.granted_by_name ?? r.grantedByName,
  granted_at: r.granted_at ?? r.grantedAt,
  starts_at: r.starts_at ?? r.startsAt,
  ends_at: r.ends_at ?? r.endsAt,
  canceled_at: r.canceled_at ?? r.canceledAt,
  status: r.status
})

export async function ExceptionsHistoryLoader({ request }) {
  const url = new URL(request.url)
  const sp = url.searchParams
  if (!sp.get('page')) sp.set('page', '1')
  if (!sp.get('per_page')) sp.set('per_page', '20')
  const { data } = await axios.get(`/api/v1/access_control_exceptions/history?${sp.toString()}`)
  return data
}

function StatusPill({ value }) {
  const color = value === 'canceled' ? 'red'
    : value === 'expired' ? 'blue-gray'
    : value === 'used' ? 'blue'
    : 'gray'
  const label = value === 'canceled' ? 'Cancelada'
    : value === 'expired' ? 'Expirada'
    : value === 'used' ? 'Usada'
    : value || '—'
  return <Chip value={label} color={color} size="sm" className="capitalize" />
}

function Pagination({ meta, searchParams, setSearchParams }) {
  if (!meta) return null
  const { page, pages } = meta
  const setPage = (p) => {
    const sp = new URLSearchParams(searchParams.toString())
    sp.set('page', String(p))
    setSearchParams(sp)
  }
  return (
    <div className="flex items-center justify-between mt-4">
      <Typography variant="small">Página {page} / {pages || '?'}</Typography>
      <div className="flex gap-2">
        <Button variant="outlined" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
        <Button disabled={pages ? page >= pages : false} onClick={() => setPage(page + 1)}>Siguiente</Button>
      </div>
    </div>
  )
}

function Toolbar({ searchParams, setSearchParams }) {
  const [local, setLocal] = useState({
    subject_type: searchParams.get('subject_type') || '',
    subject_id: searchParams.get('subject_id') || '',
    office_id: searchParams.get('office_id') || '',
    kind: searchParams.get('kind') || '',
    // fechas
    created_from: searchParams.get('created_from') || '',
    created_to: searchParams.get('created_to') || '',
    starts_from: searchParams.get('starts_from') || '',
    starts_to: searchParams.get('starts_to') || '',
    ends_from: searchParams.get('ends_from') || '',
    ends_to: searchParams.get('ends_to') || '',
    canceled_from: searchParams.get('canceled_from') || '',
    canceled_to: searchParams.get('canceled_to') || ''
  })

  const set = (k, v) => setLocal(s => ({ ...s, [k]: v }))

  const apply = () => {
    const sp = new URLSearchParams(searchParams.toString())
    Object.entries(local).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) sp.set(k, String(v))
      else sp.delete(k)
    })
    sp.set('page', '1')
    setSearchParams(sp)
  }

  const clear = () => {
    const keys = Object.keys(local)
    const sp = new URLSearchParams(searchParams.toString())
    keys.forEach(k => sp.delete(k))
    sp.set('page', '1')
    setSearchParams(sp)
    setLocal({
      subject_type: '', subject_id: '', office_id: '', kind: '',
      created_from: '', created_to: '', starts_from: '', starts_to: '',
      ends_from: '', ends_to: '', canceled_from: '', canceled_to: ''
    })
  }

  // cuando cambia el tipo, limpiamos el ID seleccionado
  const handleSubjectType = (v) => {
    set('subject_type', v || '')
    set('subject_id', '')
  }

  return (
    <Card className="mb-4">
      <CardBody className="grid gap-4">
        {/* Fila: Tipo de sujeto + Selector dependiente + Oficina */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            label="Tipo de sujeto"
            value={local.subject_type || undefined}
            onChange={handleSubjectType}
          >
            <Option value="Employee">Persona</Option>
            <Option value="Contractor::Company">Contratista</Option>
            <Option value="Contractor::Contract">Contrato</Option>
          </Select>

          <div>
            {local.subject_type === 'Employee' && (
              <EmployeeSelector
                value={local.subject_id ? Number(local.subject_id) : undefined}
                onChange={(opt) => set('subject_id', opt?.value || '')}
                required={false}
              />
            )}
            {local.subject_type === 'Contractor::Company' && (
              <CompanySelector
                value={local.subject_id ? Number(local.subject_id) : undefined}
                onChange={(opt) => set('subject_id', opt?.value || '')}
                required={false}
              />
            )}
            {local.subject_type === 'Contractor::Contract' && (
              <ContractSelector
                value={local.subject_id ? Number(local.subject_id) : undefined}
                onChange={(opt) => set('subject_id', opt?.value || '')}
                required={false}
              />
            )}
            {/* Si no hay tipo seleccionado, deja el espacio vacío */}
          </div>

          <div>
            <OfficeSelector
              value={local.office_id ? Number(local.office_id) : undefined}
              onChange={(opt) => set('office_id', opt?.value ? String(opt.value) : '')}
              isClearable
            />
          </div>
        </div>

        {/* Fila: Rango de fechas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="grid gap-2">
            <Typography variant="small" className="text-blue-gray-700">Creación</Typography>
            <Input type="datetime-local" label="Desde" value={local.created_from} onChange={e => set('created_from', e.target.value)} />
            <Input type="datetime-local" label="Hasta" value={local.created_to} onChange={e => set('created_to', e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Typography variant="small" className="text-blue-gray-700">Vigencia (inicio)</Typography>
            <Input type="datetime-local" label="Desde" value={local.starts_from} onChange={e => set('starts_from', e.target.value)} />
            <Input type="datetime-local" label="Hasta" value={local.starts_to} onChange={e => set('starts_to', e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Typography variant="small" className="text-blue-gray-700">Vigencia (término)</Typography>
            <Input type="datetime-local" label="Desde" value={local.ends_from} onChange={e => set('ends_from', e.target.value)} />
            <Input type="datetime-local" label="Hasta" value={local.ends_to} onChange={e => set('ends_to', e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Typography variant="small" className="text-blue-gray-700">Cancelación</Typography>
            <Input type="datetime-local" label="Desde" value={local.canceled_from} onChange={e => set('canceled_from', e.target.value)} />
            <Input type="datetime-local" label="Hasta" value={local.canceled_to} onChange={e => set('canceled_to', e.target.value)} />
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <Button variant="outlined" onClick={clear}>Limpiar</Button>
          <Button onClick={apply}>Aplicar</Button>
        </div>
      </CardBody>
    </Card>
  )
}
export default function AccessControlExceptionsHistoryPage() {
  const data = useLoaderData()
  const [searchParams, setSearchParams] = useSearchParams()

  const { records, meta } = useMemo(() => {
    if (!data) return { records: [], meta: null }
    if (Array.isArray(data)) return { records: data, meta: null }
    if (Array.isArray(data?.data)) return { records: data.data, meta: data.meta || null }
    const arrayKey = Object.keys(data).find(k => Array.isArray(data[k]))
    return { records: arrayKey ? data[arrayKey] : [], meta: data.meta || null }
  }, [data])

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <Typography variant="h4">Historial de Excepciones</Typography>
        <div className="flex gap-2">
          <Link to="/controldeacceso/exceptions">
            <Button variant="outlined">Volver a vigentes</Button>
          </Link>
        </div>
      </div>

      <Toolbar searchParams={searchParams} setSearchParams={setSearchParams} />

      <div className="overflow-auto rounded-lg border border-blue-gray-50">
        <table className="min-w-full text-left">
          <thead className="bg-blue-gray-50">
            <tr>
              <th className="p-3">ID</th>
              <th className="p-3">Sujeto</th>
              <th className="p-3">Dependencia</th>
              <th className="p-3">Creación</th>
              <th className="p-3">Vigencia</th>
              <th className="p-3">Estado final</th>
              <th className="p-3">Justificación</th>
              <th className="p-3">Motivo cancelación</th>
              <th className="p-3">Fecha cancelación</th>
            </tr>
          </thead>
          <tbody>
            {records?.length === 0 && (
              <tr><td className="p-3" colSpan={9}>Sin registros</td></tr>
            )}
            {records?.map(raw => {
              const r = normalize(raw)
              return (
                <tr key={r.id} className="border-b last:border-none">
                  <td className="p-3">{r.id}</td>
                  <td className="p-3">
                    <div className="flex flex-col">
                      <span className="font-medium">{r.subject_name || r.subject_type}</span>
                      <span className="text-sm text-blue-gray-600">{r.subject_subtitle}</span>
                    </div>
                  </td>
                  <td className="p-3">{r.office_name || r.office_id || 'Todas'}</td>
                  <td className="p-3">{formatDateTimeWithSeconds(r.granted_at)}</td>
                  <td className="p-3">
                    <DateRangeCell start={r.starts_at} end={r.ends_at} />
                  </td>
                  <td className="p-3"><StatusPill value={r.status} /></td>
                  <td className="p-3 max-w-[320px] truncate" title={r.justification}>{r.justification}</td>
                  <td className="p-3 max-w-[320px] truncate" title={r.cancel_reason || ''}>{r.cancel_reason || '-'}</td>
                  <td className="p-3">{formatDateTimeWithSeconds(r.canceled_at)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <Pagination meta={meta} searchParams={searchParams} setSearchParams={setSearchParams} />
    </div>
  )
}